import { useState, useEffect, useRef } from "react";
import { Users, Shield, Database, RefreshCw, AlertCircle, Loader2, Briefcase, Download, Trash2, User, Phone, Mail, UserCheck } from "lucide-react";

// Helper function to handle API responses
const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  
  // Handle HTTP errors
  if (!response.ok) {
    let errorMessage = "";
    
    switch (response.status) {
      case 400:
        errorMessage = "طلب غير صالح";
        break;
      case 401:
        errorMessage = "الصلاحية منتهية. يرجى تسجيل الدخول مرة أخرى.";
        break;
      case 403:
        errorMessage = "ليس لديك الصلاحية لهذا الإجراء.";
        break;
      case 404:
        errorMessage = "لم يتم العثور على المورد المطلوب.";
        break;
      case 409:
        errorMessage = "تعارض في البيانات.";
        break;
      case 500:
        errorMessage = "خطأ في الخادم. يرجى المحاولة لاحقاً.";
        break;
      default:
        errorMessage = `خطأ ${response.status}: ${response.statusText}`;
    }

    // Try to get more details from response body
    let errorDetails = "";
    try {
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        errorDetails = errorData.message || errorData.error || "";
      } else {
        const text = await response.text();
        if (text) errorDetails = text.substring(0, 200);
      }
    } catch (e) {
      // Ignore if we can't parse error details
    }

    throw new Error(errorDetails ? `${errorMessage} - ${errorDetails}` : errorMessage);
  }

  // If response is empty but successful (204 No Content)
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return { success: true, message: "تمت العملية بنجاح" };
  }

  // Check if response is JSON
  if (contentType && contentType.includes("application/json")) {
    return await response.json();
  } else {
    const text = await response.text();
    
    // If response is empty
    if (!text.trim()) {
      return { success: true };
    }
    
    // Try to parse as JSON anyway (in case content-type header is missing/wrong)
    try {
      return JSON.parse(text);
    } catch (err) {
      // If it's not JSON but response is OK, return success
      if (response.ok) {
        return { success: true, message: text || "تمت العملية بنجاح" };
      }
      throw new Error(`استجابة غير صالحة من الخادم: ${text.substring(0, 100)}`);
    }
  }
};

export default function ProjectsList() {
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userId: null, userName: "" });
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchCounts();
    fetchUsers();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const showMessage = (message, isError = true) => {
    if (isError) {
      setErrorMessage(message);
      setSuccessMessage("");
    } else {
      setSuccessMessage(message);
      setErrorMessage("");
    }
    
    // Auto-clear messages after 5 seconds
    setTimeout(() => {
      if (isMounted.current) {
        if (isError) {
          setErrorMessage("");
        } else {
          setSuccessMessage("");
        }
      }
    }, 5000);
  };

  const fetchCounts = async () => {
    console.log("جاري جلب الإحصائيات...");
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        showMessage("لم يتم العثور على رمز التحقق. يرجى تسجيل الدخول.");
        return;
      }

      // Fetch users count
      const usersResponse = await fetch("https://achrik-backend-2.onrender.com/api/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const usersData = await handleApiResponse(usersResponse);
      
      if (!usersData.success) {
        throw new Error(usersData.message || "فشل في جلب بيانات المستخدمين");
      }

      // Fetch admins count
      const adminsResponse = await fetch("https://achrik-backend-2.onrender.com/api/admins", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const adminsData = await handleApiResponse(adminsResponse);
      
      if (!adminsData.success) {
        throw new Error(adminsData.message || "فشل في جبل بيانات المشرفين");
      }

      // Fetch projects count
      const projectsResponse = await fetch("https://achrik-backend-2.onrender.com/api/projects", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const projectsData = await handleApiResponse(projectsResponse);
      
      if (!projectsData.success) {
        throw new Error(projectsData.message || "فشل في جلب بيانات المشاريع");
      }

      if (isMounted.current) {
        setTotalUsers(usersData.users?.length || 0);
        setTotalAdmins(adminsData.admins?.length || 0);
        setTotalProjects(projectsData.projects?.length || 0);
        console.log("تم تحديث الإحصائيات بنجاح");
      }
    } catch (err) {
      console.error("خطأ في جلب الإحصائيات:", err);
      if (isMounted.current) {
        showMessage(err.message);
        setTotalUsers(0);
        setTotalAdmins(0);
        setTotalProjects(0);
      }
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        showMessage("لم يتم العثور على رمز التحقق. يرجى تسجيل الدخول.");
        setLoading(false);
        return;
      }

      const response = await fetch("https://achrik-backend-2.onrender.com/api/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const data = await handleApiResponse(response);
      
      if (!data.success) {
        throw new Error(data.message || "فشل في جلب قائمة المستخدمين");
      }

      if (isMounted.current) {
        // Filter out password field for security
        const usersWithoutPassword = (data.users || []).map(user => {
          const { password, ...userWithoutPassword } = user;
          return userWithoutPassword;
        });
        setUsersList(usersWithoutPassword);
      }
    } catch (err) {
      console.error("خطأ في جلب المستخدمين:", err);
      if (isMounted.current) {
        showMessage(err.message);
        setUsersList([]);
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const showDeleteConfirm = (userId, userName) => {
    setDeleteConfirm({
      show: true,
      userId,
      userName
    });
  };

  const hideDeleteConfirm = () => {
    setDeleteConfirm({ show: false, userId: null, userName: "" });
  };

  const handleDeleteUser = async () => {
    const { userId, userName } = deleteConfirm;
    
    if (!userId) return;

    setDeleteLoading(prev => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("رمز التحقق غير موجود. يرجى تسجيل الدخول مرة أخرى.");
      }

      // First, let's debug what endpoint we're trying to hit
      const deleteUrl = `https://achrik-backend-2.onrender.com/api/users/${userId}`;
      console.log("محاولة حذف المستخدم من:", deleteUrl);
      console.log("معرف المستخدم:", userId);
      console.log("اسم المستخدم:", userName);

      // Add timeout for the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log("استجابة الحذف - الحالة:", response.status);
      console.log("استجابة الحذف - الحالة النصية:", response.statusText);

      const data = await handleApiResponse(response);
      
      console.log("بيانات استجابة الحذف:", data);

      if (!data.success) {
        throw new Error(data.message || "فشل في حذف المستخدم");
      }

      // Update users list
      setUsersList(prev => prev.filter(user => user._id !== userId));
      setTotalUsers(prev => prev - 1);
      
      // Show success message
      showMessage(`تم حذف المستخدم "${userName}" بنجاح`, false);
      hideDeleteConfirm();
      
    } catch (err) {
      console.error("خطأ في حذف المستخدم:", err);
      console.error("تفاصيل الخطأ:", {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      
      let errorMsg = err.message;
      if (err.name === 'AbortError') {
        errorMsg = "انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.";
      } else if (err.message.includes("404")) {
        errorMsg = "المستخدم غير موجود أو تم حذفه مسبقاً.";
      }
      
      showMessage(`فشل في حذف المستخدم: ${errorMsg}`);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [userId]: false }));
      hideDeleteConfirm();
    }
  };

  // Alternative approach: Try to fetch the user first to confirm they exist
  const handleDeleteUserWithCheck = async () => {
    const { userId, userName } = deleteConfirm;
    
    if (!userId) return;

    setDeleteLoading(prev => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("رمز التحقق غير موجود. يرجى تسجيل الدخول مرة أخرى.");
      }

      // First check if user exists by trying to fetch their data
      try {
        const checkResponse = await fetch(`https://achrik-backend-2.onrender.com/api/users/${userId}`, {
          headers: { 
            "Authorization": `Bearer ${token}`,
          },
        });

        if (checkResponse.status === 404) {
          throw new Error("المستخدم غير موجود. ربما تم حذفه مسبقاً.");
        }

        const userData = await handleApiResponse(checkResponse);
        console.log("بيانات المستخدم قبل الحذف:", userData);
      } catch (checkError) {
        if (checkError.message.includes("غير موجود")) {
          throw checkError;
        }
        // If we can't fetch but maybe they still exist, continue with deletion
        console.log("لا يمكن التحقق من وجود المستخدم، جاري المحاولة:", checkError.message);
      }

      // Try different API endpoints for deletion
      const deleteEndpoints = [
        `https://achrik-backend-2.onrender.com/api/users/${userId}`,
        `https://achrik-backend-2.onrender.com/api/user/${userId}`,
        `https://achrik-backend-2.onrender.com/api/users/delete/${userId}`
      ];

      let lastError = null;
      
      for (const endpoint of deleteEndpoints) {
        try {
          console.log("جرب حذف من:", endpoint);
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);

          const response = await fetch(endpoint, {
            method: "DELETE",
            headers: { 
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json" 
            },
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          console.log(`استجابة من ${endpoint}:`, response.status);

          if (response.ok) {
            const data = await handleApiResponse(response);
            console.log("تم الحذف بنجاح:", data);
            
            // Update users list
            setUsersList(prev => prev.filter(user => user._id !== userId));
            setTotalUsers(prev => prev - 1);
            
            // Show success message
            showMessage(`تم حذف المستخدم "${userName}" بنجاح`, false);
            hideDeleteConfirm();
            return;
          }
          
        } catch (endpointError) {
          console.log(`فشل مع ${endpoint}:`, endpointError.message);
          lastError = endpointError;
          continue; // Try next endpoint
        }
      }

      // If all endpoints failed
      if (lastError) {
        throw lastError;
      }
      
    } catch (err) {
      console.error("خطأ في حذف المستخدم:", err);
      
      let errorMsg = err.message;
      if (err.name === 'AbortError') {
        errorMsg = "انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.";
      } else if (err.message.includes("404") || err.message.includes("غير موجود")) {
        errorMsg = "المستخدم غير موجود. ربما تم حذفه مسبقاً.";
      } else if (err.message.includes("401") || err.message.includes("403")) {
        errorMsg = "ليس لديك صلاحية لحذف المستخدمين.";
      }
      
      showMessage(`فشل في حذف المستخدم: ${errorMsg}`);
      
      // If user doesn't exist, remove from local list anyway
      if (err.message.includes("غير موجود")) {
        setUsersList(prev => prev.filter(user => user._id !== userId));
        setTotalUsers(prev => prev - 1);
      }
    } finally {
      setDeleteLoading(prev => ({ ...prev, [userId]: false }));
      hideDeleteConfirm();
    }
  };

  const downloadCSV = () => {
    if (usersList.length === 0) {
      showMessage("لا توجد بيانات للتحميل");
      return;
    }

    try {
      // Prepare CSV content
      const headers = ["الاسم", "البريد الإلكتروني", "الهاتف", "الدور", "معرف المستخدم", "تاريخ الإنشاء"];
      const rows = usersList.map(user => [
        `"${user.name || ""}"`,
        `"${user.email || ""}"`,
        `"${user.phone || ""}"`,
        `"${getRoleText(user.role) || ""}"`,
        `"${user._id || ""}"`,
        `"${user.createdAt ? new Date(user.createdAt).toLocaleDateString('ar-SA') : ""}"`
      ]);

      const csvContent = [
        "\ufeff" + headers.join(","), // BOM for UTF-8 with Arabic
        ...rows.map(row => row.join(","))
      ].join("\n");

      // Create blob and download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showMessage("تم تحميل ملف CSV بنجاح", false);
    } catch (err) {
      console.error("خطأ في تحميل CSV:", err);
      showMessage("فشل في تحميل ملف CSV");
    }
  };

  const downloadJSON = () => {
    if (usersList.length === 0) {
      showMessage("لا توجد بيانات للتحميل");
      return;
    }

    try {
      const dataStr = JSON.stringify(usersList, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showMessage("تم تحميل ملف JSON بنجاح", false);
    } catch (err) {
      console.error("خطأ في تحميل JSON:", err);
      showMessage("فشل في تحميل ملف JSON");
    }
  };

  const refreshAll = () => {
    setErrorMessage("");
    setSuccessMessage("");
    fetchCounts();
    fetchUsers();
  };

  const getRoleIcon = (role) => {
    if (role === "investor") {
      return <UserCheck className="w-4 h-4 text-green-600" />;
    }
    return <User className="w-4 h-4 text-blue-600" />;
  };

  const getRoleText = (role) => {
    const roles = {
      "investor": "مستثمر",
      "admin": "مشرف",
      "user": "مستخدم"
    };
    return roles[role] || role;
  };

  // Test API connectivity
  const testApiConnection = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      console.log("Token exists:", !!token);
      
      // Test different endpoints
      const endpoints = [
        "/api/users",
        "/api/admins", 
        "/api/projects"
      ];
      
      for (const endpoint of endpoints) {
        const url = `https://achrik-backend-2.onrender.com${endpoint}`;
        console.log(`Testing ${url}...`);
        
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log(`${endpoint}: ${response.status} ${response.statusText}`);
        
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          console.log(`${endpoint} data:`, data);
        }
      }
    } catch (err) {
      console.error("API connection test failed:", err);
    }
  };

  // Uncomment to test API when component loads
  // useEffect(() => {
  //   testApiConnection();
  // }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                إحصائيات المستخدمين والمشرفين والمشاريع
              </h1>
              <div className="h-1 w-32 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
            </div>
            <button
              onClick={testApiConnection}
              className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
            >
              اختبار الاتصال
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">تأكيد الحذف</h3>
              </div>
              <p className="text-gray-600 mb-4">
                هل أنت متأكد من حذف المستخدم <span className="font-semibold text-red-600">"{deleteConfirm.userName}"</span>؟
              </p>
              <p className="text-sm text-gray-500 mb-6">هذا الإجراء لا يمكن التراجع عنه.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={hideDeleteConfirm}
                  className="px-6 py-2.5 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors duration-200"
                  disabled={deleteLoading[deleteConfirm.userId]}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleDeleteUserWithCheck} // Changed to use the improved function
                  disabled={deleteLoading[deleteConfirm.userId]}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading[deleteConfirm.userId] && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {deleteLoading[deleteConfirm.userId] ? "جاري الحذف..." : "نعم، احذف"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-md animate-fadeIn">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">تنبيه</h3>
                <p className="text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4 shadow-md animate-fadeIn">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">تم بنجاح</h3>
                <p className="text-green-700">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-green-500 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-600 font-medium">جاري تحميل البيانات...</p>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {!loading && !errorMessage && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {/* Users Card */}
              <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-green-100 hover:border-green-300">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 font-medium mb-1">إجمالي المستخدمين</p>
                      <p className="text-3xl md:text-4xl font-bold text-gray-800">{totalUsers}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Admins Card */}
              <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-green-100 hover:border-green-300">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 font-medium mb-1">إجمالي المشرفين</p>
                      <p className="text-3xl md:text-4xl font-bold text-gray-800">{totalAdmins}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Projects Card */}
              <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-green-100 hover:border-green-300">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <Briefcase className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 font-medium mb-1">إجمالي المشاريع</p>
                      <p className="text-3xl md:text-4xl font-bold text-gray-800">{totalProjects}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Total Card */}
              <div className="group bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-green-400">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-white bg-opacity-20 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <Database className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-50 font-medium mb-1">إجمالي الحسابات</p>
                      <p className="text-3xl md:text-4xl font-bold text-white">{totalUsers + totalAdmins + totalProjects}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-white bg-opacity-20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table Section */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">قائمة المستخدمين</h2>
                  <p className="text-gray-600 text-sm mt-1">إجمالي: {usersList.length} مستخدم</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={downloadCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 font-medium rounded-lg transition-colors duration-200 border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={usersList.length === 0}
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </button>
                  <button
                    onClick={downloadJSON}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium rounded-lg transition-colors duration-200 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={usersList.length === 0}
                  >
                    <Download className="w-4 h-4" />
                    JSON
                  </button>
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">الاسم</th>
                      <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">البريد الإلكتروني</th>
                      <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">الهاتف</th>
                      <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">الدور</th>
                      <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersList.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center">
                            <Users className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-lg">لا توجد بيانات للمستخدمين</p>
                            <p className="text-sm text-gray-400 mt-1">اضغط على زر التحديث لجلب البيانات</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      usersList.map((user) => (
                        <tr key={user._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-green-600" />
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-gray-800">{user.name}</p>
                                <p className="text-xs text-gray-500 mt-1">ID: {user._id?.substring(0, 8)}...</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2 justify-end">
                              <span className="text-gray-700">{user.email}</span>
                              <Mail className="w-4 h-4 text-gray-400" />
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2 justify-end">
                              <span className="text-gray-700">{user.phone || "غير محدد"}</span>
                              <Phone className="w-4 h-4 text-gray-400" />
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2 justify-end">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                user.role === "investor" 
                                  ? "bg-green-100 text-green-800" 
                                  : user.role === "admin"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {getRoleText(user.role)}
                              </span>
                              {getRoleIcon(user.role)}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex justify-end">
                              <button
                                onClick={() => showDeleteConfirm(user._id, user.name)}
                                disabled={deleteLoading[user._id]}
                                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {deleteLoading[user._id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                                <span>حذف</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={refreshAll}
                disabled={loading}
                className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                <span>تحديث جميع البيانات</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}